import type { LegacyRef, Ref } from 'react';
import { FloatingPortal } from '@floating-ui/react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';
import React from 'react';

import { isNotEmpty, isNotNull } from '@harmonizeai/poseidon-shared';
import { ButtonIcon } from '../Button';
import { cn } from '../cn';
import { buttonHeightAndBorder } from '../common';
import { Files } from '../StrokeIcons';
import InputValue from './InputValue';
import { SelectProps, SelectValueType } from './Select';
import SelectDropdownContainer from './SelectDropdownContainer';
import SelectDropdownNoResults from './SelectDropdownNoResults';
import SelectDropdownRow from './SelectDropdownRow';

export type SelectDropdownProps<T extends SelectValueType> = Omit<
  SelectProps<T>,
  'subTitle' | 'title' | 'titleClassName' | 'text'
> & {
  onCreate?: (value: T) => void;
  dropdownClassName?: string;
  resetOnChange?: boolean;
  ref?: Ref<HTMLInputElement> | LegacyRef<HTMLInputElement>;
};

const SelectDropdown = <T extends SelectValueType>({
  size,
  icon,
  error,
  value,
  className,
  dropdownClassName,
  onCreate,
  options,
  placement,
  optionRow,
  onChange,
  children,
  resetOnChange,
  ref,
  ...props
}: SelectDropdownProps<T>) => {
  const _valueOption = options?.find((o) => o.value === value || o.value === props.defaultValue);
  const [selectedOptionValue, setSelectedOptionValue] = React.useState<string | undefined>(_valueOption?.value);
  const [inputValue, setInputValue] = React.useState<string>(_valueOption?.label || '');
  const [highlightedOptionValue, setHighlightedOptionValue] = React.useState<string | undefined>(_valueOption?.value);
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const { x, y, strategy, refs } = useFloating<HTMLDivElement>({
    placement,
    middleware: [offset(4), flip()],
    whileElementsMounted: autoUpdate,
  });

  const selectOption = (el: T) => {
    if (!resetOnChange) {
      setInputValue(el.label);
      setSelectedOptionValue(el.value);
      setHighlightedOptionValue(el.value);
    }
    if (onChange) {
      onChange(el);
    }
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onBlur = () => {
    const selectedOption = options?.find((o) => o.value === selectedOptionValue);
    setInputValue(selectedOption?.label ?? '');
    setHighlightedOptionValue(undefined);
  };

  let filteredOptions: (T & { _createRow?: boolean })[] | undefined;

  const createOption = {
    icon: <Files.FilePlus02 />,
    value: inputValue,
    label: `create "${inputValue}"`,
    _createRow: true,
  } as T & { _createRow?: boolean };

  const optionsWithCreate = onCreate
    ? [...(options || []), isNotEmpty(inputValue) ? createOption : undefined].filter(isNotNull)
    : options;

  const selectedOption = optionsWithCreate?.find((o) => o.value === selectedOptionValue);

  if (selectedOption?.label === inputValue) {
    filteredOptions = optionsWithCreate || [];
  } else {
    filteredOptions = inputValue
      ? optionsWithCreate?.filter((el) => el.label?.toLowerCase().includes(inputValue.toLowerCase()))
      : optionsWithCreate;
  }

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      const currentHighlightIndex = filteredOptions?.findIndex((f) => f.value === highlightedOptionValue) ?? -1;
      const filteredOptionsLength = filteredOptions?.length ?? 0;
      const newIndex =
        currentHighlightIndex < 0 ? 0 : (currentHighlightIndex - 1 + filteredOptionsLength) % filteredOptionsLength;
      const valueToSet = filteredOptions?.[newIndex]?.value;
      setHighlightedOptionValue(valueToSet);
      refs.floating.current
        ?.querySelector(`[data-option-value="option-${valueToSet}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (e.key === 'ArrowDown') {
      const currentHighlightIndex = filteredOptions?.findIndex((f) => f.value === highlightedOptionValue) ?? -1;
      const filteredOptionsLength = filteredOptions?.length ?? 0;
      const newIndex = currentHighlightIndex < 0 ? 0 : (currentHighlightIndex + 1) % filteredOptionsLength;
      const valueToSet = filteredOptions?.[newIndex]?.value;
      setHighlightedOptionValue(valueToSet);
      refs.floating.current
        ?.querySelector(`[data-option-value="option-${valueToSet}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (e.key === 'Enter' && highlightedOptionValue) {
      const highlightedOption = optionsWithCreate?.find((o) => o.value === highlightedOptionValue);
      if (highlightedOption) {
        e.currentTarget.blur();
        selectOption(highlightedOption);
        setIsFocused(false);
      }
    }
  };

  React.useEffect(() => {
    const clickOutside = () => {
      setIsFocused(false);
    };

    if (isFocused) {
      document.addEventListener('mousedown', clickOutside);
    } else {
      document.removeEventListener('mousedown', clickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', clickOutside);
    };
  }, [isFocused]);

  return (
    <div className={className} onClick={() => setIsFocused(true)} ref={refs.setReference}>
      {children}
      <FloatingPortal>
        <SelectDropdownContainer
          className={cn('min-w-fit overflow-hidden', dropdownClassName)}
          x={x}
          y={y}
          setFloating={refs.setFloating}
          strategy={strategy}
          isOpen={isFocused}
          reference={refs.reference}
          size={size}
          onMouseDown={(e) => e.stopPropagation()}>
          <label
            onMouseDown={(e) => e.stopPropagation()}
            className={cn(
              'inline-flex overflow-hidden font-sans transition-colors',
              'bg-white focus-within:bg-apricot-white-50',
              'border-b border-slate-200 dark:border-charade-700',
              'dark:bg-charade-950 dark:focus-within:bg-charade-800',
              {
                'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-700 dark:focus-within:bg-fair-pink-900':
                  error,
              },
              buttonHeightAndBorder(size),
              'rounded-none',
            )}>
            {selectedOption?.icon == null && icon && <ButtonIcon size={size}>{icon}</ButtonIcon>}
            {selectedOption?.icon != null && <ButtonIcon size={size}>{selectedOption.icon}</ButtonIcon>}
            <InputValue
              {...props}
              value={inputValue}
              size={size}
              error={error}
              ref={ref}
              onBlur={onBlur}
              onChange={onChangeInput}
              onKeyUp={onKeyUp}
              autoFocus
            />
          </label>
          <div className="flex flex-col overflow-auto">
            {filteredOptions?.length === 0 && <SelectDropdownNoResults size={size} />}
            {filteredOptions?.map((el) => (
              <SelectDropdownRow<T>
                key={el.value}
                selectOption={selectOption}
                optionRow={el._createRow ? undefined : optionRow}
                data={el}
                setHighlightedOptionValue={setHighlightedOptionValue}
                highlightedOptionValue={highlightedOptionValue}
                size={size}
              />
            ))}
          </div>
        </SelectDropdownContainer>
      </FloatingPortal>
    </div>
  );
};

// export default SelectDropdown

export default SelectDropdown as <T extends SelectValueType>(
  props: SelectDropdownProps<T> & { ref?: Ref<HTMLInputElement> | LegacyRef<HTMLInputElement> },
) => ReturnType<typeof SelectDropdown>;
